import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  MapPin,
  ArrowRight,
  Tag,
  Layers,
  Shirt,
  Sparkles,
  Bug,
  Sun,
  Leaf,
  Lock,
  Clock,
  Wrench,
  Award,
  TrendingDown,
  Compass,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison 4 engagements ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre service de livraison de cartons {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que la préparation de vos colis demande du temps
            et de l&apos;énergie, notre service de transport gère
            toute la logistique de vos fournitures en s&apos;appuyant
            sur quatre engagements stricts.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              title: "Dépôt direct à domicile",
              body: "Nous acheminons tout votre matériel à plat devant votre porte à Herbeville, ainsi que dans les communes voisines comme Mareil-sur-Mauldre, Maule, Crespières et Alluets-le-Roi.",
              Icon: MapPin,
            },
            {
              title: "Garantie de propreté",
              body: "Vos cartons vous sont remis dans un état impeccable, totalement secs et parfaitement sains pour accueillir vos effets personnels.",
              Icon: ShieldCheck,
            },
            {
              title: "Respect de vos horaires",
              body: "Nous fixons ensemble un créneau précis pour vous éviter d'attendre et vous permettre d'avancer sans contretemps.",
              Icon: Clock,
            },
            {
              title: "Flexibilité totale",
              body: "Du kit pour petit studio au volume complet pour propriété familiale, nos stocks permanents répondent instantanément à votre besoin.",
              Icon: Layers,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white border border-[#cfdbe6] rounded-2xl p-5"
            >
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="mt-4 font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-2 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Protection vêtements anti-mites (UNIQUE) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline inline-flex items-center gap-2" style={{ color: "#237cb5" }}>
            <Shirt className="h-3.5 w-3.5" strokeWidth={2.8} />
            Garde-robe &amp; textile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment protéger vos vêtements de valeur contre les mites
            pendant le transport ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Le transfert de vos dressings, costumes et pièces de
            créateurs exige une attention particulière, notamment si
            vos effets doivent transiter par un garde-meuble ou un
            espace de stockage temporaire. Voici nos astuces pour
            sécuriser vos textiles.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            {
              num: "01",
              title: "L'usage de cartons penderies",
              body: "Nos grands cartons verticaux équipés d'une tringle métallique intégrée permettent de suspendre vos habits directement sur cintres, évitant les plis et le contact avec le sol.",
              Icon: Shirt,
              accent: "#237cb5",
            },
            {
              num: "02",
              title: "Le scellage hermétique des parois",
              body: "Appliquez une bande continue de notre ruban adhésif sur toutes les jointures du carton pour bloquer l'intrusion des nuisibles.",
              Icon: Lock,
              accent: "#237cb5",
            },
            {
              num: "03",
              title: "Les billes de cèdre naturel",
              body: "Glissez quelques morceaux de bois de cèdre ou des sachets de lavande au fond de vos cartons pour repousser naturellement les insectes.",
              Icon: Leaf,
              accent: "#FF5722",
            },
            {
              num: "04",
              title: "Le tri préalable des linges secs",
              body: "Assurez-vous que tous vos vêtements soient parfaitement secs avant la mise en boîte, car l'humidité résiduelle attire les parasites.",
              Icon: Sun,
              accent: "#FF5722",
            },
            {
              num: "05",
              title: "Les housses de protection individuelles",
              body: "Enveloppez vos manteaux et robes les plus fragiles dans des housses micro-perforées avant de les suspendre dans la penderie mobile.",
              Icon: ShieldCheck,
              accent: "#237cb5",
            },
            {
              num: "★",
              title: "Bonus : prévention nuisibles",
              body: "Notre adhésif technique haute adhérence + nos housses polyéthylène constituent ensemble une barrière physique efficace contre les insectes pendant tout le transport.",
              Icon: Bug,
              accent: "#0f1d2b",
              dark: true,
            },
          ].map(({ num, title, body, Icon, accent, dark }) => (
            <div
              key={num}
              className={`relative ${dark ? "bg-[#0f1d2b] text-white" : "bg-[#EEF3F7]"} border ${dark ? "border-[#0f1d2b]" : "border-[#cfdbe6]"} rounded-2xl p-6 overflow-hidden`}
            >
              <span
                className="absolute top-0 left-0 h-1.5 w-full"
                style={{ backgroundColor: accent === "#0f1d2b" ? "#FF5722" : accent }}
              />
              <div className="flex items-baseline gap-3">
                <span
                  className="font-display font-extrabold text-[36px] leading-none"
                  style={{ color: accent === "#0f1d2b" ? "#FF5722" : accent }}
                >
                  {num}
                </span>
                <span
                  className="flex items-center justify-center h-10 w-10 rounded-xl text-white"
                  style={{ backgroundColor: accent === "#0f1d2b" ? "#FF5722" : accent }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
              </div>
              <h3
                className={`mt-4 font-display font-extrabold text-[16.5px] leading-tight ${dark ? "text-white" : "text-[#0f1d2b]"}`}
              >
                {title}
              </h3>
              <p
                className={`mt-3 text-[13.5px] leading-relaxed ${dark ? "text-white/80" : "text-[#3a4856]"}`}
              >
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-herbeville-cta-penderie"
          >
            <Shirt className="h-4 w-4" strokeWidth={2.6} />
            Voir les cartons penderies
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-herbeville-cta-adhesif"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésif technique
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi nous choisir ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Notre promesse
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi nous choisir pour vos fournitures de déménagement ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Faire confiance à notre enseigne, c&apos;est
            l&apos;assurance de bénéficier d&apos;une expertise
            terrain unique et de services pensés exclusivement pour
            le confort de nos clients.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {[
            {
              tag: "ROBUSTESSE",
              title: "Certifiée par les professionnels du secteur",
              body: "Contrairement aux emballages standards du commerce qui se déchirent sous la tension, nos cartons à double cannelure offrent une résistance supérieure à la perforation et à la compression verticale pour sécuriser vos charges lourdes.",
              Icon: Award,
            },
            {
              tag: "RÉACTIVITÉ",
              title: "Ancrage local dans les Yvelines",
              body: "Notre parfaite connaissance de la région nous permet d'assurer des livraisons rapides, de gérer les demandes urgentes de dernière minute et de vous accompagner au plus près de votre planning.",
              Icon: Compass,
            },
            {
              tag: "RAPPORT QUALITÉ-PRIX",
              title: "En direct d'usine",
              body: "Nous éliminons les intermédiaires pour vous proposer des tarifs dégressifs et ultra-compétitifs sur l'ensemble de notre catalogue, vous permettant de vous équiper comme un pro tout en maîtrisant votre budget.",
              Icon: TrendingDown,
            },
          ].map(({ tag, title, body, Icon }) => (
            <div
              key={tag}
              className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7"
            >
              <div className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold text-[#237cb5] mb-3">
                {tag}
              </div>
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#237cb5] text-white">
                <Icon className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="mt-4 font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-herbeville-cta-shop"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Découvrir notre catalogue
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-herbeville-cta-back-dept"
        >
          <Wrench className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
